import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getCreditPackages, getCreditTransactions, createCreditCheckout } from "../../services/credit-service"
import { useToast } from "../../context/toast"
import { useCredits } from "../../context/credits"
import { useEntitlements } from "../../context/entitlements"
import { SubscriptionPlans } from "../../components/subscriptionPlans"
import { MySubscription } from "../../components/mySubscription"

// Cheia sub care reținem de unde a venit userul (ex. pagina unui candidat), ca să-l putem
// readuce și relua trimiterea după ce cumpără credite — inclusiv după redirect-ul Stripe.
const RETURN_TO_KEY = "creditsReturnTo"

const PAGE_SIZE = 20

interface CreditPackage {
  packageName: string
  label: string
  credits: number
  priceInBani: number
  originalPriceInBani: number
}

interface CreditTransaction {
  id: string
  type: string
  amount: number
  balanceAfter: number
  description: string
  createdAt: string
}

const PACKAGE_BADGE: Record<string, string> = {
  POPULAR: "Cel mai ales",
}

const formatPrice = (bani: number) => (bani / 100).toFixed(0) + " RON"

const formatDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" })
}

// Stilul fiecărui tip de tranzacție în istoric.
const TX_STYLE: Record<string, { color: string; sign: string; fallback: string }> = {
  SPEND: { color: "#FF3366", sign: "−", fallback: "Cheltuială" },
  PURCHASE: { color: "#486af6", sign: "+", fallback: "Achiziție" },
  BONUS: { color: "#486af6", sign: "+", fallback: "Bonus" },
  STIPEND: { color: "#486af6", sign: "+", fallback: "Pachet lunar de credite" },
  EXPIRE: { color: "#9ca3af", sign: "−", fallback: "Credite expirate" },
}

const minCost = (tiers?: { days: number; cost: number }[]) =>
  tiers && tiers.length ? Math.min(...tiers.map((t) => t.cost)) : null

export const CreditsPage = () => {
  const [packages, setPackages] = useState<CreditPackage[]>([])
  const [transactions, setTransactions] = useState<CreditTransaction[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [checkingOut, setCheckingOut] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const [returnTo, setReturnTo] = useState<string | null>(null)
  const { notify } = useToast()
  const navigate = useNavigate()
  const { balance, nextExpiringAt, nextExpiringAmount, pricing, lowBalance, refresh: refreshBalance, pollForChange } = useCredits()
  const { pollForActivation } = useEntitlements()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const subParam = params.get("subscription")
    const isPostPayment =
      params.get("success") === "true" || params.get("cancelled") === "true" || subParam !== null
    const rtParam = params.get("returnTo")
    if (rtParam) {
      // Venit dintr-un flux care necesită întoarcere (ex. trimite job): reținem destinația.
      localStorage.setItem(RETURN_TO_KEY, rtParam)
    } else if (!isPostPayment) {
      // Vizită normală a paginii (nu întoarcere de la Stripe): curățăm orice destinație veche.
      localStorage.removeItem(RETURN_TO_KEY)
    }
    setReturnTo(localStorage.getItem(RETURN_TO_KEY))

    if (params.get("success") === "true") {
      setSuccessMsg("Plata a fost procesată! Creditele apar în cont în câteva secunde.")
      pollForChange(balance)
    }
    if (params.get("cancelled") === "true") setErrorMsg("Plata a fost anulată.")
    if (subParam === "success") {
      setSuccessMsg("Abonament activat! Beneficiile și creditele bonus apar în câteva secunde.")
      pollForChange(balance)   // creditele bonus (stipendiu) apar prin webhook
      pollForActivation()      // planul activ + cotele incluse apar prin webhook
    }
    if (subParam === "cancelled") setErrorMsg("Abonarea a fost anulată.")
    fetchAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const goBackToFlow = () => {
    const target = localStorage.getItem(RETURN_TO_KEY) || returnTo
    localStorage.removeItem(RETURN_TO_KEY)
    if (target) navigate(target)
  }

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [pkgs, txns] = await Promise.all([
        getCreditPackages(),
        getCreditTransactions(0, PAGE_SIZE),
      ])
      refreshBalance()
      setPackages(pkgs)
      setTransactions(txns?.items ?? [])
      setHasMore(!!txns?.hasMore)
      setPage(0)
    } catch {
      setErrorMsg("Eroare la încărcarea datelor.")
    } finally {
      setLoading(false)
    }
  }

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      const next = page + 1
      const txns = await getCreditTransactions(next, PAGE_SIZE)
      setTransactions((prev) => [...prev, ...(txns?.items ?? [])])
      setHasMore(!!txns?.hasMore)
      setPage(next)
    } catch {
      notify("Eroare la încărcarea istoricului.", "error")
    } finally {
      setLoadingMore(false)
    }
  }

  const handleBuy = async (packageName: string) => {
    setCheckingOut(packageName)
    try {
      const res = await createCreditCheckout(packageName)
      if (res?.url) {
        window.location.href = res.url
      } else {
        notify("Eroare la inițierea plății. Încearcă din nou.", "error")
      }
    } catch {
      notify("Eroare la inițierea plății. Încearcă din nou.", "error")
    } finally {
      setCheckingOut(null)
    }
  }

  // „La ce folosești creditele" — derivat din tarifele backend (niciodată hardcodat).
  const usageRows = pricing
    ? [
        { label: "Postare job", value: pricing.freeJobPosts > 0 ? "primul gratuit, apoi de la " + (minCost(pricing.jobPost) ?? 0) + " credite" : "de la " + (minCost(pricing.jobPost) ?? 0) + " credite" },
        { label: "Promovare job", value: "de la " + (minCost(pricing.promoteGig) ?? 0) + " credite" },
        { label: "Promovare companie", value: "de la " + (minCost(pricing.promoteEmployer) ?? 0) + " credite" },
        { label: "Trimite job unui candidat", value: pricing.invitationCost + " credite" },
        { label: "Creare eveniment", value: pricing.eventCreate + " credite" },
        { label: "Promovare eveniment", value: "de la " + (minCost(pricing.promoteEvent) ?? 0) + " credite" },
        { label: "Notificare candidați", value: "de la " + pricing.notifyMinCost + " credite" },
        { label: "Republicare eveniment", value: pricing.republishEvent + " credite" },
      ]
    : []

  const showExpiry = !!nextExpiringAt && new Date(nextExpiringAt).getTime() > Date.now() && nextExpiringAmount > 0

  if (loading) {
    return (
      <div className="w-full px-3.5 py-3.5 sm:px-5 sm:py-5">
        <div className="animate-pulse space-y-2.5">
          <div className="h-32 rounded-[20px] bg-gray-100" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div className="h-48 rounded-[20px] bg-gray-100" />
            <div className="h-48 rounded-[20px] bg-gray-100" />
            <div className="h-48 rounded-[20px] bg-gray-100" />
          </div>
          <div className="h-40 rounded-[20px] bg-gray-100" />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full px-3.5 py-3.5 sm:px-5 sm:py-5">

      {returnTo && (
        <button
          onClick={goBackToFlow}
          className="mb-3 inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-[13px] font-bold text-figmaPrimary transition hover:bg-gray-50"
        >
          ← Înapoi și reia trimiterea
        </button>
      )}

      {successMsg && (
        <div className="mb-3 rounded-2xl bg-blue-50 border border-blue-200 px-4 py-3 text-blue-700 text-sm font-medium">
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="mb-3 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-red-600 text-sm font-medium">
          {errorMsg}
        </div>
      )}

      {/* Balance */}
      <div className="bg-white rounded-[20px] px-6 py-6 mb-2.5" style={{ border: "1px solid #e5e7eb" }}>
        <p className="text-[11px] font-bold uppercase tracking-widest text-figmaDarkGray mb-1">
          Sold credite
        </p>
        <p className="font-black text-figmaBlack leading-none" style={{ fontSize: 58, letterSpacing: "-2px" }}>
          {balance ?? 0}
        </p>
        <p className="text-[13px] text-figmaDarkGray mt-1.5">credite disponibile</p>

        {showExpiry && (
          <p className="text-[12px] text-figmaDarkGray mt-2">
            <span className="font-semibold text-figmaBlack">{nextExpiringAmount} credite</span> expiră pe {formatDate(nextExpiringAt!)}
          </p>
        )}

        {lowBalance && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
            Sold scăzut — reîncarcă pentru a putea posta și promova fără întreruperi.
          </div>
        )}
      </div>

      {/* Abonamentul meu (plan curent + contor cote rămase) — se ascunde singur fără abonament */}
      <MySubscription />

      {/* Abonamente (cote incluse recurente) — se ascunde singur dacă nu există planuri active */}
      <SubscriptionPlans />

      {/* Packages */}
      <h3 className="text-[15px] font-bold text-figmaBlack mb-1 mt-4">Cumpără credite</h3>
      <p className="text-[12px] text-figmaDarkGray mb-2.5">Plătești doar la nevoie — peste cota inclusă sau fără abonament.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
        {packages.map((pkg) => {
          const isPopular = pkg.packageName === "POPULAR"
          const badge = PACKAGE_BADGE[pkg.packageName]
          const perCredit = pkg.priceInBani / pkg.credits
          const discount = pkg.originalPriceInBani > pkg.priceInBani
            ? Math.round((1 - pkg.priceInBani / pkg.originalPriceInBani) * 100)
            : 0
          return (
            <div
              key={pkg.packageName}
              className="bg-white rounded-[20px] px-4 pt-4 pb-4 flex flex-col items-center text-center"
              style={{ border: isPopular ? "2px solid #486af6" : "1px solid #e5e7eb" }}
            >
              {badge ? (
                <span className="bg-figmaContrast text-white text-[9px] font-bold px-1.5 py-0.5 rounded mb-1.5 leading-tight">
                  {badge}
                </span>
              ) : (
                <div className="mb-1.5" style={{ height: 18 }} />
              )}
              <p className="text-[10px] font-bold uppercase tracking-wide text-figmaDarkGray leading-tight">
                {pkg.label}
              </p>
              <p className={`text-[32px] font-black leading-none mt-2 mb-0.5 ${isPopular ? "text-figmaPrimary" : "text-figmaBlack"}`}>
                {pkg.credits}
              </p>
              <p className="text-[10px] text-figmaDarkGray mb-2">credite</p>
              <div className="flex items-baseline justify-center gap-1.5">
                {discount > 0 && (
                  <span className="text-[12px] font-semibold text-figmaDarkGray line-through">
                    {formatPrice(pkg.originalPriceInBani)}
                  </span>
                )}
                <span className="text-[16px] font-black text-figmaBlack">{formatPrice(pkg.priceInBani)}</span>
              </div>
              <p className="text-[10px] text-figmaDarkGray mb-1">{(perCredit / 100).toFixed(2)} RON / credit</p>
              {discount > 0 ? (
                <span className="mb-3 inline-block rounded-full bg-figmaContrast/10 px-2 py-0.5 text-[10px] font-bold text-figmaContrast">
                  −{discount}%
                </span>
              ) : (
                <span className="mb-3" style={{ height: 18 }} />
              )}
              <button
                onClick={() => handleBuy(pkg.packageName)}
                disabled={checkingOut === pkg.packageName}
                className={`w-full py-2.5 rounded-[10px] text-[12px] font-bold transition-opacity disabled:opacity-60 ${
                  isPopular ? "bg-figmaPrimary text-white" : "bg-gray-100 text-gray-700"
                }`}
              >
                {checkingOut === pkg.packageName ? "..." : "Cumpără"}
              </button>
            </div>
          )
        })}
      </div>

      {/* La ce folosești creditele */}
      {usageRows.length > 0 && (
        <>
          <h3 className="text-[15px] font-bold text-figmaBlack mb-2.5">La ce folosești creditele</h3>
          <div className="bg-white rounded-[20px] overflow-hidden mb-5" style={{ border: "1px solid #e5e7eb" }}>
            {usageRows.map((row, i) => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-3 px-4 py-3"
                style={{ borderBottom: i < usageRows.length - 1 ? "1px solid #f3f4f6" : "none" }}
              >
                <p className="text-figmaBlack text-[13px] font-semibold">{row.label}</p>
                <p className="text-figmaDarkGray text-[13px] text-right">{row.value}</p>
              </div>
            ))}
            {pricing && pricing.creditExpiryMonths > 0 && (
              <div className="bg-figmaLightGray/60 px-4 py-2.5 text-[11px] text-figmaDarkGray">
                Creditele sunt valabile {pricing.creditExpiryMonths} luni de la achiziție.
              </div>
            )}
          </div>
        </>
      )}

      {/* Transactions */}
      <h3 className="text-[15px] font-bold text-figmaBlack mb-2.5">Istoric tranzacții</h3>
      {transactions.length === 0 ? (
        <div className="bg-white rounded-[20px] px-4 py-8 text-center" style={{ border: "1px solid #e5e7eb" }}>
          <p className="text-figmaDarkGray text-[13px]">Încă nu ai nicio tranzacție. Cumpără un pachet ca să începi.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-[20px] overflow-hidden" style={{ border: "1px solid #e5e7eb" }}>
            {transactions.map((tx, i) => {
              const style = TX_STYLE[tx.type] ?? TX_STYLE.SPEND
              return (
                <div
                  key={tx.id}
                  className="flex items-center gap-3 px-4 py-3.5"
                  style={{ borderBottom: i < transactions.length - 1 ? "1px solid #f3f4f6" : "none" }}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: style.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-figmaBlack text-[13px] font-semibold truncate">
                      {tx.description || style.fallback}
                    </p>
                    <p className="text-figmaDarkGray text-[11px] mt-0.5">{formatDate(tx.createdAt)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[16px] font-black" style={{ color: style.color }}>
                      {style.sign}{tx.amount}
                    </p>
                    <p className="text-figmaDarkGray text-[11px]">sold: {tx.balanceAfter}</p>
                  </div>
                </div>
              )
            })}
          </div>
          {hasMore && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="mt-3 w-full rounded-2xl border border-gray-200 bg-white py-3 text-[13px] font-bold text-figmaPrimary transition-opacity disabled:opacity-60"
            >
              {loadingMore ? "Se încarcă…" : "Vezi mai multe"}
            </button>
          )}
        </>
      )}

    </div>
  )
}
